import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { termsAndConditionsService } from "../api/services/termsAndConditionsService";
import {
  TermsAndConditionsFilters,
  TermsAndConditionsResult,
  TermsAndConditionsRequest,
  TermsAndConditions,
} from "../schemas/termsAndConditionsSchema";

export const useTermsAndConditions = (
  filters: TermsAndConditionsFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: termsAndConditions,
    isLoading: isTermsAndConditionsLoading,
    refetch,
  } = useQuery<TermsAndConditionsResult, Error>({
    queryKey: ["termsAndConditions", filters],
    queryFn: () => termsAndConditionsService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const uploadTermsAndConditionsMutation = useMutation({
    mutationFn: termsAndConditionsService.upload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["termsAndConditions"] });
      toast.success("Terms and Conditions uploaded successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to upload Terms and Conditions");
    },
  });

  return {
    termsAndConditions,
    refetchTermsAndConditions: refetch,
    isLoading:
      isTermsAndConditionsLoading || uploadTermsAndConditionsMutation.isPending,
    uploadTermsAndConditions: uploadTermsAndConditionsMutation.mutate,
  };
};

export const useActiveTermsAndConditions = () => {
  const {
    data: activeTermsAndConditions,
    isLoading,
    refetch,
  } = useQuery<TermsAndConditions | null, Error>({
    queryKey: ["activeTermsAndConditions"],
    queryFn: () => termsAndConditionsService.getActive(),
  });

  return {
    activeTermsAndConditions,
    isLoading,
    refetch,
  };
};
